import React, { useState, useEffect } from 'react';
import '../styles/UserManagement.css';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'viewer' });
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        setError('Failed to load users');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.username || !newUser.password) {
      setError('Username and password required');
      return;
    }

    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        setSuccess('User created successfully');
        setNewUser({ username: '', password: '', role: 'viewer' });
        loadUsers();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to create user');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      const response = await fetch(`/api/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        setSuccess('Role updated');
        loadUsers();
      } else {
        setError('Failed to update role');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword) {
      setError('New password required');
      return;
    }

    try {
      const response = await fetch(`/api/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newPassword })
      });

      if (response.ok) {
        setSuccess('Password reset successfully');
        setResetUser(null);
        setNewPassword('');
      } else {
        setError('Failed to reset password');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;

    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('User deleted');
        loadUsers();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to delete user');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  if (loading) return <div>Loading...</div>;

  const roleDescriptions = {
    admin: 'Full access - Can manage everything',
    user: 'Can view calendar and manage manual blocks',
    viewer: 'Read-only access to calendar'
  };

  return (
    <div className="user-management">
      <h2>User Management</h2>
      <p>Create and manage user accounts with different permission levels</p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="user-creation">
        <h3>Create New User</h3>
        <form onSubmit={handleCreateUser}>
          <input
            type="text"
            placeholder="Username"
            value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            required
          />
          <select
            value={newUser.role}
            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
          >
            <option value="viewer">Viewer (Read-only)</option>
            <option value="user">User (Can manage blocks)</option>
            <option value="admin">Admin (Full access)</option>
          </select>
          <button type="submit">Create User</button>
        </form>
      </div>

      <div className="users-list">
        <h3>Users</h3>
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Role</th>
              <th>Permissions</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>
                  <select
                    value={user.role || 'viewer'}
                    onChange={(e) => handleChangeRole(user.id, e.target.value)}
                    className="role-select"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td>
                  <span className="permissions">
                    {user.role === 'admin' && '✓ All permissions'}
                    {user.role === 'user' && '✓ Calendar, Blocks'}
                    {user.role === 'viewer' && '✓ View only'}
                  </span>
                </td>
                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  <button
                    className="btn-reset"
                    onClick={() => setResetUser(user.id)}
                  >
                    Reset Pass
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => handleDeleteUser(user.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {resetUser && (
        <div className="modal-overlay" onClick={() => setResetUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reset Password</h3>
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus
            />
            <div className="modal-actions">
              <button onClick={() => handleResetPassword(resetUser)} className="btn-save">
                Reset
              </button>
              <button onClick={() => setResetUser(null)} className="btn-cancel">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
